import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '../store/auth.store';
import { RETURN_URL_QUERY, ROUTES } from '../routes';
import { waitForAuthHydration } from './wait-for-auth-hydration';

/**
 * Angular functional-guard equivalent of Consumer's core/components/ProtectedRoute.tsx.
 *
 * Now *waits* for bootstrap hydration to finish (see
 * `wait-for-auth-hydration.ts`) instead of optimistically activating the
 * route while `isLoading()` is still `true` — that shortcut let
 * Dashboard/Profile/Feedback mount before `LocalStorageManagerService`
 * finished decrypting cached values (profile picture, display name, ...),
 * which is why the avatar briefly rendered a placeholder/wrong state
 * immediately after a page refresh before correcting itself a beat later.
 *
 * Unauthenticated redirects preserve `state.url` as `returnUrl` so a deep link
 * opened with an expired session lands back on that screen after login (AC7).
 */
export const protectedGuard: CanActivateFn = async (_route, state) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);

  await waitForAuthHydration(authStore);

  if (authStore.isAuthenticated()) return true;

  return router.createUrlTree([`/${ROUTES.LOGIN}`], {
    queryParams: { [RETURN_URL_QUERY]: state.url },
  });
};
